import { useParams } from "react-router-dom";
import { Button, Divider, Drawer, IconButton, InputLabel, Paper, Stack, TextField, Typography } from "@mui/material";
import { useFormik } from "formik";
import { Close } from "@mui/icons-material";
import { useEffect, useMemo, useState } from "react";
import { EMPTY_FAQ } from "./Faqs";
import { Faq, NewFaq } from "@/types/faq";
import { useAddFaqMutation, useUpdateFaqMutation } from "../../api/faqApi";
import { useAppSelector } from "@/hooks";
import { userSelector } from "@/features/auth/slice";
import WysiwygEditor from "@/components/WysiwygEditor";
import { ContentState, convertFromHTML, EditorState } from "draft-js";
import { convertToHTML } from "draft-convert";

interface Props {
  value?: Faq | NewFaq;
  onClose: () => void;
}

const myEntityToHTML = (entity, originalText) => {
  if (entity.type === 'LINK') {
    const { url } = entity.data;
    return <a alt={originalText} target="_blank" href={url}>{originalText}</a>;
  }
  // For other entities, return null or handle them as needed
  return null;
};

const AvatarDrawer = ({ value, onClose }: Props) => {
  const { organizationId } = useParams<{ organizationId: string }>();
  const isNewValue = useMemo(() => !value?.id, [value]);
  const auth = useAppSelector(userSelector);
  const [addFaq, addFaqMutation] = useAddFaqMutation();
  const [updateFaq, updateFaqMutation] = useUpdateFaqMutation();
  const [editorState, setEditorState] = useState<EditorState>();

  const formik = useFormik({
    initialValues: EMPTY_FAQ as Faq | NewFaq,
    onSubmit: async (values) => {
      const alreadyExist = "id" in values;

      if (!auth?.uid || !organizationId) {
        return;
      }

      try {
        if (alreadyExist) {
          await updateFaq({
            organizationId,
            faqId: values.id,
            faq: values,
          });
        } else {
          await addFaq({
            organizationId,
            payload: values,
          });
        }
      } catch (error) {
        console.error(error);
      } finally {
        onClose();
      }
    },
  });

  const onEditorStateChange = (e: EditorState) => {
    const html = convertToHTML({
      entityToHTML: myEntityToHTML,
    })(e.getCurrentContent());

    formik.setFieldValue("answer", html);
    setEditorState(e);
  };

  useEffect(() => {
    if (formik.values?.answer && !editorState) {
      const blocksFromHTML = convertFromHTML(formik.values?.answer);
      const state = ContentState.createFromBlockArray(blocksFromHTML.contentBlocks, blocksFromHTML.entityMap);

      setEditorState(EditorState.createWithContent(state));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values?.answer, editorState]);

  useEffect(() => {
    if (value) formik.setValues(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <Drawer
      anchor="right"
      open={!!value}
      onClose={onClose}
      PaperProps={{
        sx: {
          minWidth: 800,
        },
      }}
    >
      <Stack direction="row" justifyContent="space-between" padding={3}>
        <Typography variant="h3">{isNewValue ? "Crear Pregunta Frecuente" : "Editar Pregunta Frecuente"}</Typography>
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </Stack>
      <Divider />
      <Stack padding={"16px 32px"} spacing={2} flex={1} bgcolor={"#F2F2F2"}>
        <Paper sx={{ padding: 4 }}>
          <Stack spacing={2}>
            <TextField label="Pregunta" name="question" value={formik.values.question} onChange={formik.handleChange} fullWidth={true} />
            <TextField label="Respuesta" name="answer" value={formik.values.answer} onChange={formik.handleChange} fullWidth={true} multiline minRows={3} />
            <Stack>
              <InputLabel error={!!formik.errors.answer && !!formik.touched.answer} shrink>
                Respuesta
              </InputLabel>
              <WysiwygEditor value={editorState ?? EditorState.createEmpty()} onChange={onEditorStateChange} />
            </Stack>
          </Stack>
        </Paper>
        <Stack spacing={2} direction={"row"} justifyContent={"flex-end"}>
          <Button onClick={onClose} variant="text">
            Cancelar
          </Button>
          {isNewValue ? (
            <Button color="success" onClick={formik.handleSubmit} disabled={addFaqMutation.isLoading}>
              Crear
            </Button>
          ) : (
            <Button color="success" onClick={formik.handleSubmit} disabled={updateFaqMutation.isLoading}>
              Editar
            </Button>
          )}
        </Stack>
      </Stack>
    </Drawer>
  );
};

export default AvatarDrawer;
